import { Box, Button, Divider, IconButton, Stack } from "@mui/material";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CameraAlt, Close } from "@mui/icons-material";
import ReactTextInput from "../../../../components/ControllerComponent/ReactTextInput";
import {
  getLocationList,
  getProfileCategoryList,
} from "../../../actions/general";
import { useForm } from "react-hook-form";
import ReactSelect from "../../../../components/ControllerComponent/ReactSelect";
import ProfileImage from "../../../components/ProfileImage";
import ReactLocationInput from "../../../../components/ControllerComponent/ReactLocationInput";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ReactTelInput from "../../../../components/ControllerComponent/ReactTelInput";
import { matchIsValidTel } from "mui-tel-input";
import { Bars } from "react-loader-spinner";
import clsx from "clsx";
import { getProfileData, updateProfile } from "../../../actions/profile";
import { useMessage } from "../../../../base/MessageProvider";
import { uploadProfilePicture } from "../../../actions/auth";
import { replaceCountryCode, isValidJson } from "../../../../utils";

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("required"),
  lastName: Yup.string().required("required"),
  bio: Yup.string().optional(),
  profileCategoryId: Yup.string().required("required"),
  locations: Yup.array()
    .min(1, "Location is required")
    .max(6, "Maximum 6 locations can be added"),
  businessEmail: Yup.string().email("Invalid email").required("required"),
  businessMobileNo: Yup.string()
    .transform(replaceCountryCode)
    .length(10, "Mobile number must be 10 digit")
    .test("phone", "Invalid mobile number", (value) => /^[0-9]*$/.test(value))
    .required("Mobile number is required"),
});

const ProfileEditForm = ({ onClose, open }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const alert = useMessage();

  const [imageUrl, setImageUrl] = useState("");
  const [image, setImage] = useState(null);

  const loading = useSelector(
    (state) => state.recruiterProfileReducer?.loading
  );
  const locationList =
    useSelector((state) => state.recruiterGeneralReducer?.locationList) ?? [];
  const categoryList =
    useSelector(
      (state) => state.recruiterGeneralReducer?.profileCategoryList
    ) ?? [];
  const profileData = useSelector(
    (state) => state.recruiterProfileReducer?.profileData
  );
  const userProfile = profileData?.userProfile;

  const categoryOptions = useMemo(
    () =>
      categoryList.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [categoryList]
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { locations: [] },
  });

  const fetchInitialData = async () => {
    dispatch(
      getLocationList(
        () => {},
        (error) => {
          console.log("Error: ", error);
        }
      )
    );
  };

  useEffect(() => {
    if ((open, profileData)) {
      fetchInitialData();
      setValue("firstName", profileData?.firstName ?? "");
      setValue("lastName", profileData?.lastName ?? "");
      setValue("bio", profileData?.userProfile.bio || "");
      setValue("profileCategoryId", userProfile?.profileCategory?.id ?? "");
      setValue("businessEmail", userProfile?.businessEmail);
      setValue("businessMobileNo", userProfile?.businessMobileNo ?? "");
      setImageUrl(userProfile?.profilePic?.url);
      userProfile?.profileDetails?.forEach((profile) => {
        switch (profile.category) {
          case "Location Preferences": {
            const availableIn =
              profile.data.find((a) => a.key === "Location 1")?.value ?? [];
            if (isValidJson(availableIn))
              setValue("locations", JSON.parse(availableIn));
            else setValue("locations", []);
          }
        }
      });
    }
  }, [open, profileData]);

  const handleChange = function (e) {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
      setImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const onChooseButtonClick = () => {
    inputRef.current?.click();
  };

  const errorCallback = (error) => {
    alert.showError(error?.message ?? "Something went wrong!!");
  };

  const onSubmit = async (data) => {
    let promises = [];
    if (image) {
      const formData = new FormData();
      formData.append("file", image);

      promises.push(
        dispatch(uploadProfilePicture(formData, () => {}, errorCallback))
      );
    }
    promises.push(
      dispatch(
        updateProfile(
          { ...data, userId: profileData?.userId },
          (res) => {
            // alert.showSuccess(res?.message);
            // dispatch(getProfileData());
            // onClose();
          },
          errorCallback
        )
      )
    );

    return Promise.all(promises)
      .then((res) => {
        alert.showSuccess("Successfully Updated!!");
        dispatch(getProfileData());
        onClose();
      })
      .catch((err) => console.log(err));
  };

  return (
    <Box className="h-screen overflow-y-auto overflow-x-hidden w-full max-w-[550px] px-7">
      <Box className="w-full flex flex-col justify-between items-center pb-3 ">
        <div className="w-full px-8 flex justify-center">
          <div className="bg-[#282828] flex justify-between items-center w-[90%] text-white rounded-b-[11px] font-['CG-Medium'] text-[16px] px-4 py-3">
            <div className="flex flex-col gap-[2px] px-2">
              <h2 className="font-[Matter-Medium] font-[500] text-[16px]">
                Edit Profile
              </h2>
            </div>
            <IconButton onClick={onClose}>
              <Close className="text-white" />
            </IconButton>
          </div>
        </div>
        <Stack my={3} spacing={2} className="w-full items-center">
          <Box className="relative">
            <ProfileImage
              url={imageUrl}
              gender={userProfile?.gender}
              className="w-[100px] object-cover aspect-square border-[0.5px] border-gray-300 rounded-full"
            />
            <input
              ref={inputRef}
              className="hidden"
              type="file"
              id="image-file"
              onChange={handleChange}
              accept="image/png, image/jpeg"
              required
            />
            <IconButton
              onClick={onChooseButtonClick}
              className="bg-[#2F80ED] text-white absolute -bottom-2 right-0"
            >
              <CameraAlt className="text-lg" />
            </IconButton>
          </Box>
          <Stack
            direction={"row"}
            spacing={2}
            className="justify-between w-full relative"
          >
            <ReactTextInput
              hookFormControl={control}
              formLabel="First Name*"
              varName="firstName"
              key={"firstName"}
              placeholder="Enter first name"
              errorMessage={errors?.firstName?.message}
            />
            <ReactTextInput
              hookFormControl={control}
              formLabel="Last Name*"
              varName="lastName"
              key={"lastName"}
              placeholder="Enter last name"
              errorMessage={errors?.lastName?.message}
            />
          </Stack>
          <ReactSelect
            hookFormControl={control}
            formLabel="Profile Category*"
            varName="profileCategoryId"
            key={"profileCategoryId"}
            withSelect={false}
            placeholder="Select category"
            options={categoryOptions}
            errorMessage={errors?.profileCategoryId?.message}
          />
          <ReactLocationInput
            placeholder="Enter Location*"
            formLabel="Available In*"
            hookFormControl={control}
            varName="locations"
            multiSelect
            errorMessage={errors?.locations?.message}
            options={locationList}
          />
          <ReactTextInput
            hookFormControl={control}
            formLabel={"Bio*"}
            varName="bio"
            key={"bio"}
            isMultiLine
            rows={4}
            errorMessage={errors?.bio?.message}
          />
          <Divider />
          <ReactTextInput
            hookFormControl={control}
            formLabel="Email*"
            varName="businessEmail"
            key={"businessEmail"}
            placeholder="Enter email"
            errorMessage={errors?.businessEmail?.message}
          />
          <ReactTelInput
            hookFormControl={control}
            formLabel="Mobile No.*"
            varName="businessMobileNo"
            key={"businessMobileNo"}
            placeholder="Enter mobile no."
            errorMessage={errors?.businessMobileNo?.message}
          />
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
            className={clsx(
              "bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-lg font-['Poppins-Medium'] font-[500]",
              { "bg-opacity-70": loading }
            )}
          >
            Submit
            <Bars
              height="20"
              width={"20"}
              color="#FFFFFF"
              ariaLabel="bars-loading"
              visible={loading}
            />
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default ProfileEditForm;
